// Notifications on a phone (compact, Kareem 4 Nov): back to Today, the unread count and Dismiss all, filters as chips,
// then Today, Yesterday and Earlier with one compact row per notification: a dot when unread, a small icon (the orb
// only for the assistant), the title with the detail in two lines at most, the time, and a small dismiss. A row that
// carries a card has Approve and Ignore under it; one that asks you something has Answer; Open goes to what it is about.
import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { PLACE_FIRST, type AppNotification } from '@dpw/shared';
import { api } from '../api';
import { useDecide } from '../lib/decide';
import { useChange } from '../lib/queries';
import { duration, ease, spring } from '../lib/motion';
import { NOT_IGNORABLE, STATUS_WORD } from '../ui/ApproveCard';
import { ErrorNote } from '../ui/bits';
import { DrawnCheck, Orb, SkelList } from '../ui/motion';
import { FILTERS, NoteDetail, byDay, useNotificationList, when } from '../pages/Notifications';
import { P, Section, Title, TopBar } from './kit';

export function PhoneNotifications() {
  const { q, filter, setFilter, items, open, dismiss, dismissAll, unread } = useNotificationList();
  const decide = useDecide();
  const groups = useMemo(() => byDay(items), [items]);
  return (
    <div className="ph-page">
      <TopBar back={{ to: '/', label: 'Today' }}>
        <button type="button" className="ph-nbt" onClick={dismissAll} disabled={!unread}>Dismiss all</button>
      </TopBar>
      <Title title="Notifications" sub={open ? `${open} unread` : 'Nothing unread.'} />
      <div className="ph-chips" role="tablist" aria-label="Show">
        {FILTERS.map(([k, l]) => <button key={k} type="button" role="tab" aria-selected={filter === k} className={`ph-chip${filter === k ? ' on' : ''}`} onClick={() => setFilter(k)}>{l}</button>)}
      </div>
      {q.isLoading ? <SkelList rows={5} variant="note" /> : (
        <div className="ph-nlist">
          {groups.map(g => (
            <section key={g.day} aria-label={g.day}>
              <Section title={g.day} />
              <div className="ph-ngrp">
                <AnimatePresence initial={false}>
                  {g.items.map(n => (
                    <motion.div key={n.id} layout="position" transition={spring.gentle} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: duration.fast, ease } }}>
                      <Card n={n} decide={decide} onDismiss={() => dismiss(n)} />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </section>
          ))}
          {!items.length && <div className="ph-empty2">{filter === 'all' ? 'You are all caught up.' : 'Nothing of this kind yet.'}</div>}
          {q.hasNextPage && <button type="button" className="ph-btf sec" onClick={() => void q.fetchNextPage()} disabled={q.isFetchingNextPage}>Show older</button>}
        </div>
      )}
    </div>
  );
}

function Card({ n, decide, onDismiss }: { n: AppNotification; decide: ReturnType<typeof useDecide>; onDismiss: () => void }) {
  const [answering, setAnswering] = useState(false);
  const [answer, setAnswer] = useState('');
  const [sent, setSent] = useState(false);
  const reply = useChange((t: string) => api.checks.answer(n.teamCheckId!, t));
  const on = !n.dismissedAt;
  const canAnswer = n.payload.answerable === true && !!n.teamCheckId;
  const submit = (e: FormEvent) => { e.preventDefault(); if (answer.trim()) reply.mutate(answer.trim(), { onSuccess: () => setSent(true) }); };
  const ctx = typeof n.payload.context === 'string' && n.payload.context ? n.payload.context : null;
  const act = n.action;
  const busy = act ? decide.busy(act.id) : false;
  return (
    <div className={`ph-nt${!on ? ' gone' : ' on'}${n.isAi ? ' aiv' : ''}`} data-note={n.id} data-kind={act?.kind}>
      <span className="ud" aria-hidden="true" />{on && <span className="sr">Unread: </span>}
      {n.isAi ? <span className="ph-av ai"><Orb size={12} breathe={false} /></span>
        : n.kind === 'reminder_due' || n.kind === 'due' ? <span className="ph-av rem"><P.clock /></span>
        : <span className="ph-av" aria-hidden="true">{n.actor?.initials ?? ''}</span>}
      <div className="bd">
        <div className="l1"><div className="x"><b>{n.title}</b>{n.body ? ` ${n.body}` : ''}{ctx ? <span className="m"> · {ctx}</span> : null}</div><span className="tm">{when(n.createdAt)}</span></div>
        <NoteDetail detail={n.detail} />
        {canAnswer && on && !sent && answering && (
          <form className="ph-ansf" onSubmit={submit}>
            <input className="ph-inp" value={answer} onChange={e => setAnswer(e.target.value)} placeholder="Type your answer" aria-label="Answer" autoFocus />
            <button className="ph-btm pri" disabled={reply.isPending || !answer.trim()}>Send</button>
          </form>
        )}
        {reply.error ? <ErrorNote error={reply.error} /> : null}
        {(act || canAnswer || n.link) && <div className="ph-nacts">
          {act && (act.status === 'waiting'
            ? (NOT_IGNORABLE.has(act.kind)
              ? <Link className="ph-btm pri" to={`/requests?id=${act.id}`}>{act.kind === 'confirm_answer' ? 'Choose the answer' : 'Review the answer'}</Link>
              : PLACE_FIRST.has(act.kind) ? <Link className="ph-btm pri" to={`/requests?id=${act.id}`}>Log it</Link>
              : <>
                <button type="button" className="ph-btm pri" disabled={busy} onClick={() => decide.approve({ ...act, title: n.title })}>Approve</button>
                <button type="button" className="ph-btm" disabled={busy} onClick={() => decide.ignore({ ...act, title: n.title })}>Ignore</button>
              </>)
            : <span className={act.status === 'approved' ? 'ph-okd2 st' : 'f13 mu st'}>{act.status === 'approved' && <DrawnCheck size={13} />}{STATUS_WORD[act.status]}</span>)}
          {canAnswer && on && !sent && !answering && <button type="button" className="ph-btm pri" onClick={() => setAnswering(true)}>Answer</button>}
          {sent && <span className="ph-okd2"><DrawnCheck size={15} />Answer sent</span>}
          {(n.link || act) && <Link className="lk" to={act && act.status === 'waiting' && !n.link ? `/requests?id=${act.id}` : n.link ?? '/requests'}>Open</Link>}
        </div>}
      </div>
      {/* Dismiss is a small icon on every row (no hover on a phone). */}
      {on ? <button type="button" className="ph-nx" onClick={onDismiss} aria-label="Dismiss" title="Dismiss"><P.x /></button> : <span className="ph-nx done sr">Dismissed</span>}
    </div>
  );
}
